// src/pages/Dashboard/Admin/DetailsAdherents/hooks/useAdherentDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllAdherentsWithGroupAndUsers,
  getAdherentById,
  getUserIdByAdherentId,
} from "../../../../../services/adherentServices";

import { getSettings } from "../../../../../services/settingsServices";
import { getGroupsList } from "../../../../../services/groupServices";
import { getDojos } from "../../../../../services/dojoServices";
import { getRoles } from "../../../../../services/userServices";

import { getBeltHistoryByAdherent } from "../../../../../services/beltServices";
import { getPalmaresByAdherentId } from "../../../../../services/palmaresServices";
import { getEventsByAdherent } from "../../../../../services/eventServices";
import { getDossierInscription } from "../../../../../services/dossierServices";
import { getReglementInscriptionByIdAndSaison } from "../../../../../services/reglementinscriptionServices";
import { getReglementsByUser } from "../../../../../services/achatsServices";
import { getAttendanceList } from "../../../../../services/attendanceServices";

import getCategory from "../../../../../utils/getCategory";
import getWeightCategory from "../../../../../utils/getWeightCategory";

import {
  calculateAdherentDashboardStats,
  getSeasonsAround,
  mergeAdherentListMeta,
  safeArray,
  sortAdherentsByName,
  unwrapSettings,
} from "../utils/adherentDashboardUtils";

export function useAdherentDashboard() {
  const [settings, setSettings] = useState(null);

  const [adherents, setAdherents] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dojos, setDojos] = useState([]);

  const [seasons, setSeasons] = useState([]);
  const [seasonFilter, setSeasonFilter] = useState("");

  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [selectedAdherent, setSelectedAdherent] = useState(null);

  const [roles, setRoles] = useState([]);
  const [managers, setManagers] = useState([]);
  const [dossier, setDossier] = useState(null);
  const [reglement, setReglement] = useState(null);
  const [belts, setBelts] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [palmares, setPalmares] = useState([]);
  const [achats, setAchats] = useState([]);
  const [attendances, setAttendances] = useState([]);

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchBaseData = useCallback(async () => {
    try {
      setLoadingPage(true);
      setErrorMsg("");

      const [adherentsData, settingsData, groupsData, dojoList] =
        await Promise.all([
          getAllAdherentsWithGroupAndUsers(),
          getSettings(),
          getGroupsList(),
          getDojos(),
        ]);
      console.log("[DetailsAdherentDashboard] adherentsData:", adherentsData);
      console.log(
        "[DetailsAdherentDashboard] normalized adherents:",
        safeArray(adherentsData),
      );

      const adherentsRows = sortAdherentsByName(safeArray(adherentsData));
      const allGroupsRows = safeArray(groupsData);
      const dojoRows = safeArray(dojoList);
      const clubSettings = unwrapSettings(settingsData);

      setAdherents(adherentsRows);
      setAllGroups(allGroupsRows);
      setDojos(dojoRows);
      setSettings(clubSettings);

      if (dojoRows.length === 1) {
        const onlyId = String(dojoRows[0].id);
        setSelectedDojo(onlyId);
        setGroups(
          allGroupsRows.filter((group) => String(group.dojo_id) === onlyId),
        );
      } else {
        setGroups(allGroupsRows);
      }

      const activeSeason = clubSettings?.saison_active || "";
      setSeasons(getSeasonsAround(activeSeason));
      setSeasonFilter(activeSeason || "");
    } catch (error) {
      console.error("Erreur chargement dashboard adhérent :", error);
      setErrorMsg("Impossible de charger les données de base des adhérents.");
      toast.error("Erreur lors du chargement des adhérents.");
    } finally {
      setLoadingPage(false);
    }
  }, []);

  useEffect(() => {
    fetchBaseData();
  }, [fetchBaseData]);

  const handleDojoChange = useCallback(
    (event) => {
      const dojoId = event.target.value;

      setSelectedDojo(dojoId);
      setSelectedGroup("");

      if (!dojoId) {
        setGroups(allGroups);
        return;
      }

      setGroups(
        allGroups.filter((group) => String(group.dojo_id) === String(dojoId)),
      );
    },
    [allGroups],
  );

  const filteredAdherents = useMemo(() => {
    const selectedGroupValue = String(selectedGroup || "").trim();
    const selectedDojoValue = String(selectedDojo || "").trim();

    const dojoGroups = selectedDojoValue ? safeArray(groups) : [];

    const dojoGroupNames = new Set(
      dojoGroups
        .map((group) => String(group?.nom || "").trim())
        .filter(Boolean),
    );

    return sortAdherentsByName(
      safeArray(adherents).filter((adherent) => {
        const seasonsOfAdherent = [
          adherent?.saison_active,
          adherent?.saison,
          adherent?.saison_inscription,
        ]
          .filter(Boolean)
          .map(String);

        const matchSeason =
          !seasonFilter || seasonsOfAdherent.includes(String(seasonFilter));

        const adherentGroups = safeArray(adherent?.Groups);

        const matchDojo =
          !selectedDojoValue ||
          adherentGroups.some((group) => {
            const groupName = String(group?.nom || "").trim();
            return dojoGroupNames.has(groupName);
          });

        const matchGroup =
          !selectedGroupValue ||
          adherentGroups.some((group) => {
            const groupName = String(group?.nom || "").trim();
            return groupName === selectedGroupValue;
          });

        return matchSeason && matchDojo && matchGroup;
      }),
    );
  }, [adherents, seasonFilter, selectedDojo, selectedGroup, groups]);

  const clearDetails = useCallback(() => {
    setSelectedAdherent(null);
    setManagers([]);
    setDossier(null);
    setReglement(null);
    setBelts([]);
    setCompetitions([]);
    setPalmares([]);
    setAchats([]);
    setAttendances([]);
  }, []);

  const fetchAchatsForManagers = useCallback(async (managerRows) => {
    const rows = [];

    for (const manager of safeArray(managerRows)) {
      const userId =
        manager?.UserAdherent?.user_id || manager?.id || manager?.user_id;

      if (!userId) continue;

      try {
        const response = await getReglementsByUser(userId);
        rows.push(...safeArray(response));
      } catch (error) {
        console.error("Erreur achats gestionnaire :", error);
      }
    }

    const dedup = new Map();

    rows.forEach((item, index) => {
      const key = item?.id
        ? `id:${item.id}`
        : `${item?.date_achat || "date"}:${item?.prix_total || 0}:${index}`;

      dedup.set(key, item);
    });

    return Array.from(dedup.values()).sort(
      (a, b) =>
        new Date(b?.date_achat || 0).getTime() -
        new Date(a?.date_achat || 0).getTime(),
    );
  }, []);

  const loadAdherentDetails = useCallback(
    async (adherentId) => {
      if (!adherentId) return;

      try {
        setLoadingDetails(true);
        setErrorMsg("");

        const fromList = adherents.find(
          (item) => String(item.id) === String(adherentId),
        );

        const [
          adherentResponse,
          managersResponse,
          rolesResponse,
          beltsResponse,
          palmaresResponse,
          competitionsResponse,
          dossierResponse,
          attendancesResponse,
        ] = await Promise.allSettled([
          getAdherentById(adherentId),
          getUserIdByAdherentId(adherentId),
          getRoles(),
          getBeltHistoryByAdherent(adherentId),
          getPalmaresByAdherentId(adherentId),
          getEventsByAdherent(adherentId),
          getDossierInscription(adherentId),
          getAttendanceList(adherentId),
        ]);

        const adherentData =
          adherentResponse.status === "fulfilled"
            ? adherentResponse.value
            : null;

        const mergedAdherent = mergeAdherentListMeta(
          adherentData || fromList,
          fromList,
        );

        const managerRows =
          managersResponse.status === "fulfilled"
            ? safeArray(managersResponse.value)
            : [];

        const roleRows =
          rolesResponse.status === "fulfilled"
            ? safeArray(rolesResponse.value)
            : [];

        const beltRows =
          beltsResponse.status === "fulfilled"
            ? safeArray(beltsResponse.value)
            : [];

        const palmaresRows =
          palmaresResponse.status === "fulfilled"
            ? safeArray(palmaresResponse.value)
            : [];

        const competitionsData =
          competitionsResponse.status === "fulfilled"
            ? competitionsResponse.value
            : [];

        const competitionRows = Array.isArray(competitionsData?.Events)
          ? competitionsData.Events
          : safeArray(competitionsData);

        const dossierData =
          dossierResponse.status === "fulfilled"
            ? dossierResponse.value || null
            : null;

        const attendanceRows =
          attendancesResponse.status === "fulfilled"
            ? safeArray(attendancesResponse.value)
            : [];

        const saison =
          mergedAdherent?.saison_active ||
          seasonFilter ||
          settings?.saison_active ||
          "";

        let reglementData = null;

        if (saison) {
          try {
            reglementData = await getReglementInscriptionByIdAndSaison(
              adherentId,
              saison,
            );
          } catch (error) {
            console.error("Erreur règlement adhérent :", error);
          }
        }

        const achatsRows = await fetchAchatsForManagers(managerRows);

        setSelectedAdherent(mergedAdherent || null);
        setManagers(managerRows);
        setRoles(roleRows);
        setBelts(beltRows);
        setPalmares(palmaresRows);
        setCompetitions(competitionRows);
        setDossier(dossierData);
        setReglement(reglementData || null);
        setAttendances(attendanceRows);
        setAchats(achatsRows);
      } catch (error) {
        console.error("Erreur chargement détails adhérent :", error);
        setErrorMsg("Impossible de charger la fiche complète de l’adhérent.");
        toast.error("Erreur lors du chargement de l’adhérent.");
      } finally {
        setLoadingDetails(false);
      }
    },
    [adherents, fetchAchatsForManagers, seasonFilter, settings],
  );

  const selectAdherent = useCallback(
    async (adherentId) => {
      const id = String(adherentId || "");
      setSelectedAdherentId(id);

      if (!id) {
        clearDetails();
        return;
      }

      await loadAdherentDetails(id);
    },
    [clearDetails, loadAdherentDetails],
  );

  useEffect(() => {
    if (selectedAdherentId) {
      loadAdherentDetails(selectedAdherentId);
    }
  }, [seasonFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshCurrentAdherent = useCallback(async () => {
    await fetchBaseData();

    if (selectedAdherentId) {
      await loadAdherentDetails(selectedAdherentId);
    }
  }, [fetchBaseData, loadAdherentDetails, selectedAdherentId]);

  const ageCategory = useMemo(() => {
    if (!selectedAdherent?.date_naissance) return "—";
    return getCategory(selectedAdherent.date_naissance) || "—";
  }, [selectedAdherent]);

  const weightCategory = useMemo(() => {
    if (!selectedAdherent) return "—";

    return (
      getWeightCategory(
        selectedAdherent.genre,
        ageCategory,
        selectedAdherent.poids,
      ) || "—"
    );
  }, [selectedAdherent, ageCategory]);

  const stats = useMemo(
    () =>
      calculateAdherentDashboardStats({
        adherent: selectedAdherent,
        managers,
        dossier,
        reglement,
        belts,
        competitions,
        palmares,
        achats,
        attendances,
      }),
    [
      selectedAdherent,
      managers,
      dossier,
      reglement,
      belts,
      competitions,
      palmares,
      achats,
      attendances,
    ],
  );

  return {
    settings,
    adherents,
    filteredAdherents,

    seasons,
    seasonFilter,
    setSeasonFilter,

    dojos,
    selectedDojo,
    handleDojoChange,

    allGroups,
    groups,
    selectedGroup,
    setSelectedGroup,

    selectedAdherentId,
    selectedAdherent,
    selectAdherent,
    refreshCurrentAdherent,

    roles,
    managers,
    dossier,
    reglement,
    belts,
    competitions,
    palmares,
    achats,
    attendances,

    ageCategory,
    weightCategory,
    stats,

    loadingPage,
    loadingDetails,
    errorMsg,
  };
}
